import { useTranslation } from "next-i18next";
import { useCallback, useState } from "react";

import { Button } from "@/components/elements/Button";
import { Media } from "@/components/elements/Media";
import { Text } from "@/components/elements/Text";
import { NumberStepper } from "@/components/NumberStepper";
import { ProductInfoBlock } from "@/components/ProductInfoBlock";
import { CartItem, useCart } from "@/hooks/useCart";
import useDialog from "@/hooks/useDialog";
import { useInventory } from "@/hooks/useInventory";
import useLocaleHelper from "@/hooks/useLanguageHelper";
import { useProducts } from "@/hooks/useProducts";
import { formatDate } from "@/utils/format";

import styles from "./styles.module.css";

export type DialogRefillProps = {
  item: CartItem;
  dateRefilled: string;
  quantityRefilled: number;
};

export const DialogRefill = ({
  item,
  dateRefilled,
  quantityRefilled,
}: DialogRefillProps) => {
  const { t } = useTranslation();
  // Get product info
  const { id: productId } = item;
  const { data: productsData } = useProducts();
  const { data: inventoryData } = useInventory();
  const [quantity, setQuantity] = useState<number>(1);
  const { dismissDialog } = useDialog();
  const { currentLanguage } = useLocaleHelper();
  const { addItem } = useCart();

  const productsById = productsData?.productsById || {};
  const productQuantitiesBySku = inventoryData?.productQuantitiesBySku || {};
  const product = productsById[productId];
  const availableQuantity =
    productQuantitiesBySku[product?.sku_code]?.available_quantity;
  const dateRefilledFormatted = formatDate({
    locale: currentLanguage,
    date: dateRefilled,
    format: "MMM DD",
  });
  const addRefillToCart = useCallback(() => {
    dismissDialog();
    addItem(
      {
        id: product.id,
        price: product.price,
        quantity,
      },
      "products"
    );
  }, [dismissDialog, item, quantity]);
  // Define last refilled message
  const lastRefilledMessage =
    quantityRefilled > 1
      ? t("order_overview_add_last_pl", {
          date: dateRefilledFormatted,
          quantity: quantityRefilled,
        })
      : t("order_overview_add_last_sg", {
          date: dateRefilledFormatted,
          quantity: quantityRefilled,
        });
  return (
    <div className={styles.container}>
      <div className={styles.productDetail}>
        <div className={styles.figureContainer}>
          <Media
            src={product.product_image}
            objectFit="cover"
            alt={t(product.title)}
            aspectRatio="square"
          />
        </div>
        <div className={styles.productInfo}>
          <ProductInfoBlock product={product} />
          <Text size="xxs" className={styles.orderInfo}>
            {lastRefilledMessage}
          </Text>
        </div>
      </div>
      <div className={styles.buttonContainer}>
        <NumberStepper
          name={t(product.title)}
          minValue={1}
          maxValue={availableQuantity}
          value={quantity}
          onChange={(value) => {
            setQuantity(value);
          }}
          label={`${t("order_detail_quantity")} ${t(product.title)}`}
          hideLabel
          className={styles.numberStepper}
        />
        <Button
          onClick={addRefillToCart}
          className={styles.addButton}
          disabled={!availableQuantity}
        >
          {t("add_CTA")}
        </Button>
      </div>
    </div>
  );
};
